import { Theme, Typography } from '@mui/material';
import { Variant } from '@mui/material/styles/createTypography';
import { SxProps } from '@mui/system';
import { Duration, intervalToDuration } from 'date-fns-jalali';
import { FC, useEffect, useState } from 'react';

import { HBIcon } from '../HBIcon/HBIcon';
import { HBCountDownTimerRootStyle, RefreshCodeStyle } from './HBCountDownTimer.styles';

export type HBCountDownTimerProps = {
  targetDate: number | Date;
  linkText: string;
  onClick?: () => void;
  disabledButton?: boolean;
  extraTimerText?: string;
  sx?: SxProps<Theme>;
  maximumShowed?: 'second' | 'minute' | 'hours' | 'days';
  onFinished?: () => void;
  textVariant?: Variant;
  dateNow?: number;
};

const maximumShowedArray = ['second', 'minute', 'hours', 'days'];

export const HBCountDownTimer: FC<HBCountDownTimerProps> = (props) => {
  const {
    extraTimerText,
    targetDate: targetDateProps,
    linkText,
    onClick,
    sx,
    maximumShowed = 'days',
    onFinished,
    textVariant = 'overline',
    dateNow,
  } = props;
  const [targetDate, setTargetDate] = useState<number | Date | undefined>(targetDateProps);
  const remaining = () => {
    const now = dateNow || new Date();
    const end = new Date(targetDate as number);
    if (now < end)
      return intervalToDuration({
        start: now,
        end,
      });
    else return { hours: 0, minutes: 0, seconds: 0, days: 0 };
  };

  const padWithZero = (num: number | undefined) => {
    if (num === undefined) return '00';
    return num < 10 ? `0${num}` : String(num);
  };

  useEffect(() => {
    if (targetDateProps) setTargetDate(targetDateProps);
  }, [targetDateProps]);

  const maximumShowedIndex = maximumShowedArray.findIndex((i) => i === maximumShowed);
  const [timer, setTimer] = useState<Duration>(remaining());
  const { hours = 0, minutes = 0, seconds = 0, days = 0 } = timer;

  const handleClick = (): void => {
    if (onClick) {
      onClick();
      return;
    }
    if (props.disabledButton) return;
  };

  useEffect(() => {
    if (targetDate) {
      const timerInterval = setInterval(() => {
        const now = new Date();
        const end = new Date(targetDate as number);
        if (now < end)
          setTimer(
            intervalToDuration({
              start: now,
              end,
            }),
          );
        else {
          setTimer({ hours: 0, minutes: 0, seconds: 0, days: 0 });
          clearInterval(timerInterval);
          onFinished?.();
        }
      }, 1000);
      return () => clearInterval(timerInterval);
    }
    return () => null;
  }, [targetDate]);

  if (days + hours + minutes + seconds <= 0 && linkText)
    return (
      <RefreshCodeStyle
        className={props.disabledButton ? 'refresh-btn-disabled' : 'refresh-btn'}
        onClick={handleClick}
        data-test-id="refresh-btn"
      >
        <HBIcon size="small" type="historyAlt" />
        <Typography variant={textVariant} className="resend-code">
          {linkText}
        </Typography>
      </RefreshCodeStyle>
    );
  if (days + hours + minutes + seconds <= 0) {
    return null;
  }
  return (
    <HBCountDownTimerRootStyle sx={{ ...sx }}>
      {maximumShowedIndex >= 3 && !!days && (
        <>
          <Typography variant={textVariant} sx={{ textAlign: 'center' }}>
            {days ? days.toString() : '00'}
          </Typography>
          <Typography>: </Typography>
        </>
      )}

      {maximumShowedIndex >= 2 && !!hours && (
        <>
          <Typography variant={textVariant} sx={{ textAlign: 'center' }}>
            {' '}
            {hours
              ? padWithZero(hours + (maximumShowed === 'hours' && !!days ? days * 24 : 0))
              : '00'}
          </Typography>
          <Typography>: </Typography>
        </>
      )}

      {maximumShowedIndex >= 1 && !!minutes && (
        <>
          <Typography variant={textVariant} sx={{ textAlign: 'center' }}>
            {' '}
            {minutes ? padWithZero(minutes) : '00'}
          </Typography>
          <Typography>: </Typography>
        </>
      )}

      {maximumShowedIndex >= 0 && (
        <Typography variant={textVariant} sx={{ textAlign: 'center' }}>
          {' '}
          {seconds ? padWithZero(seconds) : '00'}{' '}
        </Typography>
      )}
      {extraTimerText && (
        <Typography
          variant={textVariant}
          component={'span'}
          sx={{ color: (theme) => theme.palette.grey[500], mr: (theme) => theme.spacing(2) }}
        >
          {extraTimerText}
        </Typography>
      )}
    </HBCountDownTimerRootStyle>
  );
};

HBCountDownTimer.displayName = 'HBCountDownTimer';
